/* global bootstrap */
import HelperMixin from '../../../Shared/HelperMixin.vue'
import { visibleGroups, searchIndex, scoreRow, groupKeyForPath, itemMatchesPath, normalizeAr } from './menu.js'
import { guideFor } from '../../../Shared/pageGuides.js'

/** Hover must be deliberate: a hint that fires while the pointer is merely
 *  crossing the menu on its way somewhere else is noise, not help. */
const HINT_DELAY_MS = 260;
const HINT_WIDTH = 330;

const RAFIQ_OPEN_GROUP_KEY = 'rafiq.sidebar.openGroup';
const RAFIQ_COLLAPSED_KEY = 'rafiq.sidebar.collapsed';

export default {
    name: 'appHeader',
    mixins: [HelperMixin],

    async created() {
        this.loginDetails = await this.CheckLoginStatus();

        if (this.loginDetails) {
            if (this.loginDetails.userType != 1
                && this.loginDetails.userType != 2
                && this.loginDetails.userType != 3
                && this.loginDetails.userType != 40
                && this.loginDetails.userType != 41
                && this.loginDetails.userType != 50) {
                this.logout();
            }

            // لو مستخدم مدرسة نجيب schoolId ونخزنه
            if (this.loginDetails.userType === 40 || this.loginDetails.userType === 41) {
                this.$http.GetMySchool()
                    .then(res => {
                        if (res && res.data && res.data.id) {
                            localStorage.setItem('schoolId', String(res.data.id));
                            localStorage.setItem('schoolName', res.data.name || '');
                        }
                    })
                    .catch(() => { /* silent — header just hides the chip */ });
            }

            // School users start on the Schools group
            if (this.loginDetails.userType == 40 || this.loginDetails.userType == 41) {
                this.openGroup = 'schools';
            } else {
                // route wins over the stored group: landing directly on /CommissionRules
                // must open التسعير والعمولات, not whatever was open last session.
                const fromRoute = this.inferGroupFromRoute();
                const stored = localStorage.getItem(RAFIQ_OPEN_GROUP_KEY);
                this.openGroup = fromRoute || stored || 'overview';
            }
        } else {
            this.logout();
        }

        // restore collapsed state
        const collapsed = localStorage.getItem(RAFIQ_COLLAPSED_KEY);
        this.isSidebarClosed = collapsed === '1';
        this.applySidebarBodyAttr();

        window.scrollTo(0, 0);
    },

    mounted() {
        this.$nextTick(() => {
            this.initTooltips();
        });
        document.addEventListener('keydown', this.onGlobalKeydown);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this.onGlobalKeydown);
        this.clearHintTimer();
        this.clearHideTimer();
    },

    data() {
        return {
            openGroup: 'overview',
            isSidebarClosed: false,
            query: '',
            hint: null,
            hintTop: 0,
            hintRight: 0,
            hintTimer: null,
            hideTimer: null
        };
    },

    computed: {
        /** Groups this user may see — derived from menu.js, not hand-written markup. */
        groups() {
            return visibleGroups(this.loginDetails ? this.loginDetails.userType : 0);
        },

        /**
         * Flat search hits. Every term must match somewhere, and a hit in the
         * page title outranks a hit in a synonym — so "عمولة" puts
         * قواعد العمولة first, not الإيرادات (which lists it as a keyword).
         */
        results() {
            const terms = normalizeAr(this.query).split(' ').filter(Boolean);
            if (!terms.length) return [];
            const index = searchIndex(this.loginDetails ? this.loginDetails.userType : 0);
            return index
                .map(row => ({ row: row, score: scoreRow(row, terms) }))
                .filter(hit => hit.score > 0)
                .sort((a, b) => b.score - a.score)
                .map(hit => hit.row);
        },

        hintStyle() {
            return { top: this.hintTop + 'px', right: this.hintRight + 'px', width: HINT_WIDTH + 'px' };
        }
    },

    watch: {
        // When route changes, auto-open the group containing the active route
        '$route.path'(newPath) {
            const inferred = this.inferGroupFromRoute(newPath);
            if (inferred && inferred !== this.openGroup) {
                this.openGroup = inferred;
                this.persistOpenGroup();
            }
        }
    },

    methods: {
        href(url) {
            this.hideHint();
            if (url !== this.$router.currentRoute.path) {
                this.$router.push(url);
            }
            // Clear the search so the accordion (with the new page highlighted)
            // comes back instead of leaving the admin staring at stale results.
            this.query = '';
        },

        toggleGroup(groupKey) {
            // Accordion: one open at a time. Click on already-open group toggles closed.
            this.openGroup = (this.openGroup === groupKey) ? null : groupKey;
            this.persistOpenGroup();
        },

        isActive(item) {
            return itemMatchesPath(item, this.$route ? this.$route.path : '');
        },

        groupHasActive(group) {
            return group.items.some(item => this.isActive(item));
        },

        persistOpenGroup() {
            try {
                if (this.openGroup) {
                    localStorage.setItem(RAFIQ_OPEN_GROUP_KEY, this.openGroup);
                } else {
                    localStorage.removeItem(RAFIQ_OPEN_GROUP_KEY);
                }
            } catch (_) { /* ignore */ }
        },

        /**
         * Map current route path to a sidebar group key so the right accordion
         * auto-opens on direct navigation / page reload. Resolved from menu.js,
         * so adding a page to the menu wires this up automatically.
         */
        inferGroupFromRoute(path) {
            const p = path || (this.$route && this.$route.path) || '';
            return groupKeyForPath(p, this.loginDetails ? this.loginDetails.userType : 0);
        },

        /**
         * Show the rich hint for a menu entry, anchored beside the link.
         * `position: fixed` (not absolute) so the scrolling menu body cannot
         * clip it, and so the panel stays put while the list scrolls under it.
         */
        showHint(item, event) {
            this.clearHintTimer();
            const anchor = event && event.currentTarget;
            if (!anchor) return;
            this.hintTimer = setTimeout(() => {
                const rect = anchor.getBoundingClientRect();
                const guide = guideFor(item.path);
                if (!guide) return;
                // RTL: the sidebar hugs the right edge, so the panel opens to the
                // LEFT of the link — rect.left is the sidebar's inner edge.
                this.hintRight = Math.max(8, window.innerWidth - rect.left + 10);
                this.hintTop = Math.max(12, rect.top - 8);
                this.hint = guide;
                // Panels vary from ~150px to ~400px tall depending on how much
                // the page has to explain, so the bottom edge can only be kept
                // on screen once the real height is known.
                this.$nextTick(() => {
                    const panel = this.$refs.hint;
                    if (!panel) return;
                    const height = panel.offsetHeight;
                    const maxTop = window.innerHeight - height - 12;
                    if (this.hintTop > maxTop) this.hintTop = Math.max(12, maxTop);
                });
            }, HINT_DELAY_MS);
        },

        /**
         * Pointer moved onto the panel itself. There is a 10px gap between the
         * link and the panel, so leaving the link fires mouseleave first —
         * hiding is therefore deferred, and landing on the panel cancels it.
         */
        keepHint() {
            this.clearHideTimer();
        },

        hideHint() {
            this.clearHintTimer();
            this.clearHideTimer();
            this.hideTimer = setTimeout(() => { this.hint = null; }, 140);
        },

        clearHintTimer() {
            if (this.hintTimer) {
                clearTimeout(this.hintTimer);
                this.hintTimer = null;
            }
        },

        clearHideTimer() {
            if (this.hideTimer) {
                clearTimeout(this.hideTimer);
                this.hideTimer = null;
            }
        },

        clearQuery() {
            this.query = '';
        },

        openFirstResult() {
            if (this.results.length) this.href(this.results[0].item.path);
        },

        /** "/" focuses the sidebar search from anywhere (unless already typing). */
        onGlobalKeydown(e) {
            if (e.key !== '/' || e.ctrlKey || e.altKey || e.metaKey) return;
            const tag = (e.target && e.target.tagName ? e.target.tagName : '').toLowerCase();
            if (tag === 'input' || tag === 'textarea' || tag === 'select' || (e.target && e.target.isContentEditable)) return;
            if (this.isSidebarClosed) return;
            const box = this.$refs.searchInput;
            if (box) {
                e.preventDefault();
                box.focus();
            }
        },

        initTooltips() {
            if (typeof bootstrap === 'undefined') {
                setTimeout(() => this.initTooltips(), 200);
                return;
            }
            const elements = document.querySelectorAll('[data-bs-toggle="tooltip"]');
            elements.forEach(el => {
                if (!el._tooltip) {
                    el._tooltip = new bootstrap.Tooltip(el);
                }
            });
        },

        applySidebarBodyAttr() {
            document.body.setAttribute(
                'data-sidebartype',
                this.isSidebarClosed ? 'mini-sidebar' : 'full'
            );
        },

        toggleSidebar() {
            this.isSidebarClosed = !this.isSidebarClosed;
            if (this.isSidebarClosed) this.query = '';
            try {
                localStorage.setItem(RAFIQ_COLLAPSED_KEY, this.isSidebarClosed ? '1' : '0');
            } catch (_) { /* ignore */ }
            this.applySidebarBodyAttr();
        }
    }
}
